import {ChangeDetectionStrategy, Component, inject, signal, computed} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';
import {ChallengeReward} from 'src/app/core/model/project';
import {AddRewardAction, UpdateRewardAction} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {selectChallengeBuilderProject} from 'src/app/core/store/challenge-builder/challenge-builder.selector';
import {MessageService} from 'src/app/services/message.service';
import {CbDialogFooterComponent, CbDialogHeaderComponent} from '../cb-dialog-parts.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';

interface DialogData {
  reward?: ChallengeReward;
}

@Component({
  selector: 'cb-reward-dialog',
  standalone: true,
  imports: [FormsModule, MatIconModule, CbDialogHeaderComponent, CbDialogFooterComponent, OphXButtonComponent],
  templateUrl: './cb-reward-dialog.component.html',
  styleUrl: './cb-reward-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbRewardDialogComponent {
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  readonly dialogRef = inject(MatDialogRef<CbRewardDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA, {optional: true});

  project = this.store$.selectSignal(selectChallengeBuilderProject);

  name = signal(this.data?.reward?.name || '');
  description = signal(this.data?.reward?.description || '');
  rewardCost = signal<number | null>(this.data?.reward?.rewardCost ?? null);
  loading = signal(false);

  readonly maxDescriptionLength = 140;

  isEditMode = computed(() => this.data?.reward !== undefined);
  titleVariable = computed(() => (this.isEditMode() ? 'Edit a' : 'New'));
  dialogTitle = computed(() => (this.isEditMode() ? 'Edit Reward' : 'New Reward'));

  descriptionLength = computed(() => this.description().length);

  saveDisabled = computed(() => {
    const titleTrimmed = this.name().trim();
    const costValue = this.rewardCost();
    return !titleTrimmed || costValue === null || costValue <= 0;
  });

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const projectId = this.project()?._id;
    const nameValue = this.name().trim();
    const descriptionValue = this.description().trim();
    const costValue = this.rewardCost();

    if (!projectId || !nameValue || costValue === null || costValue <= 0) {
      return;
    }

    this.loading.set(true);

    const reward: ChallengeReward = {
      ...this.data?.reward,
      name: nameValue,
      description: descriptionValue,
      rewardCost: costValue,
    };

    if (this.isEditMode()) {
      this.store$.dispatch(
        new UpdateRewardAction({
          projectId,
          reward,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    } else {
      this.store$.dispatch(
        new AddRewardAction({
          projectId,
          reward,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    }
  }

  private onSaveSuccess(): void {
    this.loading.set(false);
    this.dialogRef.close(true);
  }

  private onSaveFailure(error: Error): void {
    this.loading.set(false);
    this.messageService.add(error.message || 'Failed to add reward', 'error');
  }
}
